import { useTranslation } from "react-i18next";
import {
  Megaphone,
  Newspaper,
  ShieldCheck,
  TrendingUp,
  Compass,
  Flag,
  GraduationCap,
  ArrowRight,
} from "lucide-react";
import LocaleLink from "@/components/LocaleLink";
import { pickTodayItem } from "@/data/dailyTrailItems";

const ICONS = {
  sparkles: Megaphone,
  newspaper: Newspaper,
  "shield-check": ShieldCheck,
  "trending-up": TrendingUp,
  compass: Compass,
  rocket: Flag,
  "graduation-cap": GraduationCap,
} as const;

const TodayOnTheTrail = () => {
  const { t, i18n } = useTranslation();
  const { item, index } = pickTodayItem();
  const Icon = ICONS[item.icon];

  // Locale-aware ISO date for the "TODAY · 27 Apr 2026" rail.
  const today = new Date();
  const dateLabel = new Intl.DateTimeFormat(i18n.language || "en-US", {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(today);

  const label = t(`dailyTrail.items.${index}.label`, "Trail update");
  const message = t(`dailyTrail.items.${index}.message`, "");
  const todayLabel = t("dailyTrail.todayLabel");
  const readMore = t("dailyTrail.readMore");

  return (
    <aside
      aria-label={t("dailyTrail.ariaLabel")}
      className="border-y border-border border-t-2 border-t-accent/40 bg-secondary"
    >
      <div className="container mx-auto px-6">
        <LocaleLink
          to={item.href}
          className="group flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:gap-4"
        >
          <div className="flex items-center gap-2 shrink-0">
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider bg-accent text-accent-foreground">
              {todayLabel}
            </span>
            <span className="text-xs text-muted-foreground tabular-nums">
              {dateLabel}
            </span>
          </div>
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Icon className="h-4 w-4 text-primary shrink-0" />
            <span className="text-sm font-medium text-foreground truncate">
              {label}
            </span>
            {message && (
              <span className="text-sm text-muted-foreground truncate hidden md:inline">
                · {message}
              </span>
            )}
          </div>
          <span className="inline-flex items-center gap-1 text-xs font-medium text-primary shrink-0 group-hover:gap-2 transition-all">
            {readMore}
            <ArrowRight className="h-3 w-3" />
          </span>
        </LocaleLink>
      </div>
    </aside>
  );
};

export default TodayOnTheTrail;
